/**
 * §P.2 · La ficha de un grupo: sus datos, su horario y sus matriculados.
 *
 * **Cerrar un grupo no lo borra**, y el botón lo dice. De él cuelgan las sesiones
 * del curso y la asistencia de cada niño: borrarlo dejaría el historial sin nombre
 * —«asistió a algo, tres martes de octubre»— y ese historial es lo que sostiene la
 * barra de progreso de la app de familias. Un grupo cerrado deja de generar
 * clases, que es lo que el club quiere decir al pulsar.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/client';
import { etagDe } from '@/shared/api/concurrencia';
import { aProblema, ErrorDeApi } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { rangoDeEdad, ocupacion, type Grupo } from '@/club/grupos/tipos';
import {
  puedeEditarAlumnos,
  puedeEditarProfesores,
  puedeVerProfesores,
  type Rol,
} from '@/club/permisos';
import { ProfesoresDelGrupo } from '@/club/grupos/ProfesoresDelGrupo';
import { FormularioDeGrupo } from '@/club/grupos/FormularioDeGrupo';
import { HorarioSemanal } from '@/club/grupos/HorarioSemanal';
import { Matriculados } from '@/club/grupos/Matriculados';

export function FichaDeGrupo({
  club,
  grupoId,
  rol,
  suspendido,
  alCerrar,
  alAbrirAlumno,
}: {
  club: string;
  grupoId: string;
  rol: Rol;
  suspendido: boolean;
  alCerrar: () => void;
  alAbrirAlumno: (alumnoId: string) => void;
}) {
  const cache = useQueryClient();
  const puedeEscribir = puedeEditarAlumnos(rol);
  const [editando, setEditando] = useState(false);
  const [confirmandoCierre, setConfirmandoCierre] = useState(false);

  const grupo = useQuery({
    queryKey: ['grupo', club, grupoId],
    queryFn: async () => {
      const r = await api.v1.clubs({ club }).groups({ groupId: grupoId }).get();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return { grupo: r.data as unknown as Grupo, etag: etagDe(r) };
    },
  });

  const cerrarGrupo = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .groups({ groupId: grupoId })
        .delete();
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setConfirmandoCierre(false);
      void cache.invalidateQueries({ queryKey: ['grupos', club] });
      void cache.invalidateQueries({ queryKey: ['grupo', club, grupoId] });
    },
  });

  if (grupo.isPending)
    return <Cargando filas={4} etiqueta="Cargando el grupo" />;

  if (grupo.isError)
    return (
      <PanelError
        problema={aProblema(grupo.error)}
        reintentar={() => void grupo.refetch()}
      />
    );

  const g = grupo.data.grupo;
  const puedeTocar = puedeEscribir && !suspendido;

  return (
    <section className="ficha">
      <header className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Grupos
        </button>
        {puedeTocar && !editando && (
          <div className="titulo-acciones">
            <button
              type="button"
              className="btn-plano"
              onClick={() => setEditando(true)}
            >
              Editar
            </button>
            {g.active && (
              <button
                type="button"
                className="btn-plano"
                onClick={() => setConfirmandoCierre(true)}
              >
                Cerrar grupo
              </button>
            )}
          </div>
        )}
      </header>

      {editando ? (
        <FormularioDeGrupo
          club={club}
          grupo={g}
          etag={grupo.data.etag}
          suspendido={suspendido}
          alGuardar={() => setEditando(false)}
          alCancelar={() => setEditando(false)}
        />
      ) : confirmandoCierre ? (
        <div className="hueco hueco-confirmar">
          <h2>¿Cerrar «{g.name}»?</h2>
          <p>Dejará de generar clases nuevas a partir de ahora.</p>
          <ul className="lista-clara">
            <li>
              <strong>No se borra nada.</strong> Las clases que ya están y la
              asistencia de cada niño siguen exactamente igual: es lo que
              sostiene su historial de progreso.
            </li>
            <li>
              <strong>Se puede volver a abrir</strong> desde «Editar».
            </li>
          </ul>

          {cerrarGrupo.isError && (
            <PanelError problema={aProblema(cerrarGrupo.error)} />
          )}

          <div className="hueco-acciones">
            <button
              type="button"
              disabled={cerrarGrupo.isPending}
              onClick={() => cerrarGrupo.mutate()}
            >
              {cerrarGrupo.isPending ? 'Cerrando…' : 'Sí, cerrar el grupo'}
            </button>
            <button
              type="button"
              className="btn-plano"
              disabled={cerrarGrupo.isPending}
              onClick={() => setConfirmandoCierre(false)}
            >
              Cancelar
            </button>
          </div>
        </div>
      ) : (
        <>
          <h1>
            {g.name}
            {!g.active && (
              <span className="pastilla pastilla-left"> Cerrado</span>
            )}
          </h1>

          <dl className="datos">
            <div>
              <dt>Sede</dt>
              <dd>{g.venue_name ?? '—'}</dd>
            </div>
            <div>
              <dt>Nivel</dt>
              <dd>{g.level ?? '—'}</dd>
            </div>
            <div>
              <dt>Edades</dt>
              <dd>{rangoDeEdad(g) ?? '—'}</dd>
            </div>
            <div>
              <dt>Ocupación</dt>
              <dd>{ocupacion(g)}</dd>
            </div>
          </dl>

          <h2 className="seccion">Horario</h2>
          <HorarioSemanal
            club={club}
            grupoId={grupoId}
            puedeEditar={puedeTocar}
          />

          {/*
            Al entrenador no se le enseña: es organización del club, y además
            `GET /coaches` le responde `403`.
          */}
          {puedeVerProfesores(rol) && (
            <>
              <h2 className="seccion">Quién lo da</h2>
              <ProfesoresDelGrupo
                club={club}
                grupoId={grupoId}
                puedeEditar={puedeEditarProfesores(rol) && !suspendido}
              />
            </>
          )}

          <h2 className="seccion">Matriculados</h2>
          <Matriculados
            club={club}
            grupo={g}
            puedeEditar={puedeTocar}
            alAbrirAlumno={alAbrirAlumno}
          />
        </>
      )}
    </section>
  );
}
